import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Eye, Clock, Shield, CheckCircle, AlertTriangle, Calendar, Phone, Award, ArrowRight, BookOpen, ExternalLink } from 'lucide-react';

const Pterigio = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const benefits = [
    { icon: Eye, title: 'Vista Più Nitida', description: 'Rimozione della membrana che può oscurare la cornea e causare astigmatismo' },
    { icon: Shield, title: 'Prevenzione Progressione', description: 'Intervento che evita l\'avanzamento verso il centro della cornea' },
    { icon: Clock, title: 'Recupero Veloce', description: 'Ritorno alle attività normali in pochi giorni' },
    { icon: CheckCircle, title: 'Basso Rischio Recidiva', description: 'Tecniche moderne con autotrapianto congiuntivale' },
  ];

  const riskFactors = [
    'Esposizione prolungata ai raggi UV (sole)',
    'Lavoro all\'aperto (agricoltori, marinai, edili)',
    'Clima secco, ventoso e polveroso',
    'Irritazione cronica degli occhi',
    'Predisposizione genetica',
    'Età tra i 20 e i 50 anni'
  ];

  const symptoms = [
    'Crescita di tessuto carnoso rosa/rosso sulla congiuntiva',
    'Arrossamento cronico dell\'occhio',
    'Sensazione di corpo estraneo',
    'Bruciore e prurito',
    'Lacrimazione eccessiva',
    'Visione offuscata (nei casi avanzati)',
    'Astigmatismo indotto dalla trazione sulla cornea'
  ];

  const treatments = [
    {
      name: 'Terapia Conservativa',
      description: 'Per pterigi piccoli e asintomatici',
      details: [
        'Lacrime artificiali per lubrificazione',
        'Colliri antinfiammatori al bisogno',
        'Protezione UV con occhiali da sole',
        'Monitoraggio periodico della crescita'
      ],
      indication: 'Pterigio < 2mm, asintomatico'
    },
    {
      name: 'Chirurgia con Autotrapianto',
      description: 'Gold standard per la rimozione definitiva',
      details: [
        'Escissione completa del pterigio',
        'Autotrapianto di congiuntiva dalla stessa congiuntiva sana',
        'Fissazione con colla biologica o suture',
        'Tasso di recidiva molto basso (< 5%)'
      ],
      indication: 'Pterigio in crescita o sintomatico'
    },
    {
      name: 'Chirurgia con Membrana Amniotica',
      description: 'Alternativa all\'autotrapianto',
      details: [
        'Utilizzo di membrana amniotica umana',
        'Proprietà antinfiammatorie e rigenerative',
        'Ideale per pterigi recidivanti',
        'Ottimo risultato estetico'
      ],
      indication: 'Recidive o pterigi estesi'
    }
  ];

  const faq = [
    {
      question: 'Il pterigio è pericoloso per la vista?',
      answer: 'Il pterigio di per sé è una lesione benigna. Tuttavia, se non trattato e lasciato progredire, può invadere la cornea centrale, causando astigmatismo irregolare e compromettendo la qualità della visione. Per questo è importante monitorarlo e intervenire quando indicato.'
    },
    {
      question: 'L\'intervento è doloroso?',
      answer: 'L\'intervento viene eseguito in anestesia locale topica (collirio anestetico) e dura circa 20-30 minuti. Non si avverte dolore durante la procedura. Nei giorni successivi può esserci un moderato fastidio, ben controllabile con antidolorifici comuni.'
    },
    {
      question: 'Qual è il rischio di recidiva?',
      answer: 'Con le tecniche moderne di autotrapianto congiuntivale, il rischio di recidiva è molto basso, inferiore al 5%. Le tecniche più vecchie (escissione semplice) avevano tassi di recidiva fino al 50-80%.'
    },
    {
      question: 'Quando posso tornare al lavoro?',
      answer: 'La maggior parte dei pazienti può riprendere le attività normali dopo 2-3 giorni. È consigliabile evitare ambienti polverosi, vento e sole diretto per 2-3 settimane. L\'uso di occhiali da sole è fortemente raccomandato.'
    },
    {
      question: 'Il pterigio può ritornare?',
      answer: 'Sì, il pterigio può recidivare, soprattutto se l\'esposizione ai fattori di rischio (sole, vento, polvere) continua. La prevenzione con occhiali da sole UV e lacrime artificiali riduce significativamente questo rischio.'
    }
  ];

  const scientificArticles = [
    {
      title: 'Pterigio: Epidemiologia, Fattori di Rischio e Trattamento',
      authors: 'Rezvan F, Khabazkhoob M, Hooshmand E, et al.',
      journal: 'Journal of Ophthalmic & Vision Research',
      year: '2018',
      doi: '10.4103/jovr.jovr_91_17',
      summary: 'Review completa su epidemiologia, fattori di rischio e opzioni terapeutiche del pterigio.'
    },
    {
      title: 'Autotrapianto Congiuntivale vs Trapianto di Membrana Amniotica per il Pterigio',
      authors: 'Clearfield E, Muthappan V, Wang X, Kuo IC.',
      journal: 'Cochrane Database of Systematic Reviews',
      year: '2016',
      doi: '10.1002/14651858.CD006966.pub2',
      summary: 'Meta-analisi che confronta le tecniche chirurgiche: autotrapianto vs membrana amniotica.'
    },
    {
      title: 'Il Ruolo dell\'Esposizione UV nello Sviluppo del Pterigio',
      authors: 'Coroneo MT, Di Girolamo N, Wakefield D.',
      journal: 'Experimental Eye Research',
      year: '1999',
      doi: '10.1006/exer.1999.0714',
      summary: 'Studio fondamentale sul ruolo della radiazione UV nella patogenesi del pterigio.'
    },
    {
      title: 'Gestione Chirurgica del Pterigio: Revisione Basata sull\'Evidenza',
      authors: 'Hirst LW.',
      journal: 'Ophthalmology',
      year: '2003',
      doi: '10.1016/S0161-6420(03)00316-8',
      summary: 'Revisione delle evidenze scientifiche sulle tecniche chirurgiche per il pterigio.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232A9FCB' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ background: 'rgba(42, 159, 203, 0.1)', color: '#2A9FCB' }}
              >
                Patologie della Superficie Oculare
              </span>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Pterigio
              </h1>
              <p 
                className="text-lg sm:text-xl mb-8 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif", color: '#666666' }}
              >
                Il pterigio è una crescita benigna di tessuto fibrovascolare sulla congiuntiva 
                che può estendersi sulla cornea. Diagnosticato precocemente, può essere 
                trattato efficacemente con tecniche chirurgiche mini-invasive a basso rischio di recidiva.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/prenota" 
                  className="inline-flex items-center justify-center bg-[#2A9FCB] text-white hover:bg-[#1C729E] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Prenota Visita
                </Link>
                <a 
                  href="tel:+390123456789" 
                  className="inline-flex items-center justify-center border-2 border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#E6F4FA] rounded-full px-8 py-4 text-sm font-medium transition-all duration-300"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Chiama Ora
                </a>
              </div>
            </div>
            
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <img 
                src="/api/static/treatments/pterigio.png"
                alt="Pterigio - Crescita sulla congiuntiva"
                className="rounded-3xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '500px' }}
              />
              <div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl"
                style={{ maxWidth: '280px' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#E6F4FA] flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#2A9FCB]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2A9FCB]">&lt;5%</p>
                    <p className="text-sm text-gray-500">Tasso di Recidiva</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Con autotrapianto congiuntivale</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Pterigio Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 
                className="text-3xl sm:text-4xl mb-6"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Cos'è il Pterigio?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Il pterigio (dal greco "pterygion" = piccola ala) è una crescita anomala di tessuto 
                congiuntivale che si sviluppa tipicamente nell'angolo interno dell'occhio (lato nasale) 
                e può progressivamente invadere la cornea.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                È una patologia molto comune nelle regioni con forte esposizione solare, tanto da essere 
                chiamata anche "occhio del surfista" o "occhio del contadino".
              </p>
              <div className="bg-[#FFF8E1] border-l-4 border-[#FFC107] p-4 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#FFC107] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">Attenzione</p>
                    <p className="text-gray-600 text-sm">
                      Se noti una crescita anomala sulla superficie dell'occhio, consulta un oculista. 
                      La diagnosi precoce permette un trattamento più efficace.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#1A1A1A' }}>Sintomi Comuni</h3>
              <ul className="space-y-3">
                {symptoms.map((symptom, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2A9FCB] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{symptom}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Vantaggi del Trattamento
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Perché è importante trattare il pterigio
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="p-8 rounded-2xl bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-transparent hover:border-[#2A9FCB]/20"
              >
                <div className="w-14 h-14 rounded-full bg-[#E6F4FA] flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#2A9FCB]" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Factors Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-[#2A9FCB] to-[#1C729E] rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-semibold mb-6">Fattori di Rischio</h3>
                <ul className="space-y-4">
                  {riskFactors.map((factor, idx) => (
                    <li key={idx} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-white" />
                      </div>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 
                className="text-3xl sm:text-4xl mb-6"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Chi è a Rischio?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Il pterigio è più comune in persone che vivono in climi caldi e soleggiati, 
                vicino all'equatore. L'esposizione cronica ai raggi UV è il principale fattore di rischio.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Anche il vento, la polvere e l'aria secca contribuiscono all'irritazione cronica 
                della congiuntiva che favorisce lo sviluppo del pterigio.
              </p>
              <div className="bg-[#E6F4FA] p-6 rounded-2xl">
                <p className="font-semibold text-[#2A9FCB] mb-2">Prevenzione</p>
                <p className="text-gray-700">
                  Indossare sempre occhiali da sole con protezione UV 400 quando si è all'aperto, 
                  anche nelle giornate nuvolose. Usare lacrime artificiali per mantenere l'occhio lubrificato.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Opzioni di Trattamento
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Approcci personalizzati in base alla gravità del pterigio
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {treatments.map((treatment, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#2A9FCB] flex items-center justify-center text-white font-bold">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: '#1A1A1A' }}>{treatment.name}</h3>
                </div>
                <p className="text-gray-600 mb-6">{treatment.description}</p>
                <ul className="space-y-3 mb-6">
                  {treatment.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2A9FCB] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">Indicazione</p>
                  <p className="font-semibold text-[#2A9FCB]">{treatment.indication}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Articles Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="w-8 h-8 text-[#2A9FCB]" />
              <h2 
                className="text-3xl sm:text-4xl"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Letteratura Scientifica
              </h2>
            </div>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#666666' }}>
              Articoli e studi di riferimento sul pterigio
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {scientificArticles.map((article, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{article.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{article.authors}</p>
                <p className="text-sm text-[#2A9FCB] mb-3">
                  {article.journal}, {article.year}
                </p>
                <p className="text-gray-600 text-sm mb-4">{article.summary}</p>
                <a 
                  href={`https://doi.org/${article.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-[#2A9FCB] hover:underline"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  DOI: {article.doi}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Domande Frequenti
            </h2>
          </div>
          
          <div className="space-y-4">
            {faq.map((item, idx) => (
              <details 
                key={idx}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 group"
              >
                <summary className="p-6 cursor-pointer flex items-center justify-between font-semibold text-[#1A1A1A] hover:text-[#2A9FCB] transition-colors">
                  {item.question}
                  <ArrowRight className="w-5 h-5 transform group-open:rotate-90 transition-transform text-[#2A9FCB]" />
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2A9FCB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl text-white mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Hai Notato una Crescita sull'Occhio?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Non aspettare che il pterigio avanzi. Prenota una visita per una valutazione 
            accurata e scopri le opzioni di trattamento più adatte al tuo caso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/prenota" 
              className="inline-flex items-center justify-center bg-white text-[#2A9FCB] hover:bg-gray-100 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300 shadow-lg"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Prenota Visita
            </Link>
            <a 
              href="tel:+390123456789" 
              className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-4 text-sm font-semibold transition-all duration-300"
            >
              <Phone className="w-5 h-5 mr-2" />
              Chiama: 012 345 6789
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pterigio;
